//src/apis/chatApi.ts
import HttpClient from "./httpClient";
import { API_URLS } from "../utils/apiUrls";
import { HANDLE_MESSAGE_HTTP_TIMEOUT_MS } from "../constants/requestTimeouts";
import type { AutomationCapacity, CustomerInteraction } from "../types/integration";

export interface HandleMessageAutomationOptions {
    automation_capacity?: AutomationCapacity;
    customer_interaction?: CustomerInteraction;
    /**
     * This message's `automation_capacity` (plain chat included) becomes the thread's mode. Without
     * it, a message that names no mode inherits the thread's.
     */
    automation_override?: boolean;
    /**
     * The agent to run as, by slug, when this message starts Auto Resolver. The server loads that
     * agent's config; without it the ticket's matching agent (or the default) is used.
     */
    agent_id?: number;
}

/** The mode a thread is in: what its next message runs as when it names none. */
export interface ThreadAutomation {
    automation_capacity: AutomationCapacity;
    customer_interaction: CustomerInteraction;
    /** The agent the thread's Auto Resolver run belongs to; empty in plain chat. */
    agent_id?: number;
}

export type TicketResolverState = "running" | "waiting" | "success" | "cancelled" | "declined";

export interface ThreadResolverState {
    resolver_state: TicketResolverState | null;
    resolver_context?: { on?: "technician" | "system"; kind?: string; reason?: string } | null;
    updated_at?: string | null;
}

export interface ChatAttachment {
    filename: string;
    content_type: string;
    data: string;
    size_bytes?: number;
}

export enum AssistantMode {
    CHAT = 0,
    DEEP_RESEARCH = 1,
}

export interface HandleMessageOptions {
    automation?: HandleMessageAutomationOptions;
    assistantMode?: AssistantMode;
    attachments?: ChatAttachment[];
}

export interface CreateUserThreadOptions {
    researchEnabled?: boolean;
}

export interface GetUserThreadsOptions {
    researchOnly?: boolean;
}

export interface FileLinearTaskRequest {
    title: string;
    description?: string;
    ticket_link: string;
    assignee_email: string;
    trace_url?: string;
}

export interface FileLinearTaskResponse {
    issue_id: string;
    identifier: string;
    url: string;
    title: string;
}

export interface LinearTaskAssignee {
    id: string;
    name: string;
    email: string;
}

/** In-memory cache entries for audit data used by ToolAuditLogPopup. */
function getCached<T>(cache: Map<string, { data: T; ts: number }>, key: string): T | undefined {
    const entry = cache.get(key);
    return entry ? entry.data : undefined;
}

function setCached<T>(cache: Map<string, { data: T; ts: number }>, key: string, data: T): void {
    cache.set(key, { data, ts: Date.now() });
}

const threadLogsCache = new Map<string, { data: unknown; ts: number }>();
const ticketThreadsCache = new Map<string, { data: unknown; ts: number }>();
const ticketLogsCache = new Map<string, { data: unknown; ts: number }>();


class ChatApi extends HttpClient {
    protected url;

    constructor() {
        super();
        this.url = API_URLS;
    }

    public getTicketThread = (ticketId: string) =>
        this.getCoalesced(`/ai-threads/ticket/${ticketId}`);

    /** List past threads for a ticket (ticket-only, user_id null). For thread dropdown in ticket chat. */
    public getTicketThreads = async (ticketId: string) => {
        const cached = getCached(ticketThreadsCache, ticketId);
        if (cached !== undefined) return cached;
        const data = await this.get(API_URLS.GET_TICKET_THREADS.replace(":ticketId", ticketId));
        setCached(ticketThreadsCache, ticketId, data);
        return data;
    };

    public createTicketThread = (ticketId: string) => 
        this.post(this.url.CREATE_TICKET_THREAD, {}, { ticket_id: ticketId });

    /** LLM summary of the ticket and its active Theo thread for AI Overview (can be slow; uses extended timeout). */
    public fetchTicketAiOverview = (
        ticketId: string,
        ticketContext: string,
        customerId?: number | string | null,
        aiThreadId?: string | null
    ) =>
        this.post(
            this.url.TICKET_AI_OVERVIEW,
            { timeout: 120000 },
            {
                ticket_id: ticketId,
                ticket_context: ticketContext,
                ...(aiThreadId ? { ai_thread_id: String(aiThreadId) } : {}),
                ...(customerId !== undefined &&
                customerId !== null &&
                customerId !== "" &&
                !Number.isNaN(Number(customerId))
                    ? { customer_id: Number(customerId) }
                    : {}),
            }
        ) as Promise<{
          overview_lines: string[];
          user_lifecycle?: string | null;
        }>;

    /** LLM QA review of a full ticket thread (can be slow; uses extended timeout). */
    public fetchTicketQualityReview = (
        ticketId: string,
        ticketContext: string,
        customerId?: number | string | null
    ) =>
        this.post(
            this.url.TICKET_QUALITY_REVIEW,
            { timeout: 180000 },
            {
                ticket_id: ticketId,
                ticket_context: ticketContext,
                ...(customerId !== undefined &&
                customerId !== null &&
                customerId !== "" &&
                !Number.isNaN(Number(customerId))
                    ? { customer_id: Number(customerId) }
                    : {}),
            }
        ) as Promise<{
          summary: string;
          positives: string[];
          issues: string[];
        }>;

    public handleMessage = (
        aiThreadId: string,
        userId: string,
        content: string,
        structuredWorkflow?: Record<string, unknown>,
        requestId?: string,
        options?: HandleMessageOptions
    ) => {
        const request_id = requestId || `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const request: Record<string, unknown> = { content };
        const attachments = options?.attachments;
        if (Array.isArray(attachments) && attachments.length > 0) {
            request.attachments = attachments;
        }
        if (structuredWorkflow && Object.keys(structuredWorkflow).length > 0) {
            request.structured_workflow_json = JSON.stringify(structuredWorkflow);
        }
        if (options?.automation?.automation_override === true) {
            // An explicit mode, plain chat included, so the capacity is sent even when it is 0.
            request.automation_capacity = options.automation.automation_capacity ?? 0;
            request.automation_override = true;
        } else if (options?.automation?.automation_capacity !== undefined && options.automation.automation_capacity > 0) {
            request.automation_capacity = options.automation.automation_capacity;
        }
        if (options?.automation?.agent_id && Number(request.automation_capacity) > 0) {
            request.agent_id = options.automation.agent_id;
        }
        if (
            options?.automation?.customer_interaction !== undefined &&
            options.automation.customer_interaction > 0
        ) {
            request.customer_interaction = options.automation.customer_interaction;
        }
        request.assistant_mode = options?.assistantMode ?? AssistantMode.CHAT;
        // So "3pm" means the technician's 3pm: Cortex reads clock times in this zone.
        try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timeZone) request.technician_time_zone = timeZone;
        } catch {
            // No Intl time zone: Cortex falls back to UTC.
        }

        const requestBody = {
            ai_thread_id: aiThreadId,
            user_id: userId,
            request_id: request_id,
            request,
        };

        const send = () =>
            this.post(
                this.url.HANDLE_MESSAGE,
                { timeout: HANDLE_MESSAGE_HTTP_TIMEOUT_MS },
                requestBody
            );
        return send().catch((error: { response?: unknown }) => {
            // A dropped POST is retried with the same request_id so the server returns the existing turn.
            if (error?.response) throw error;
            return send();
        });
    };

    public cancelTurn = (aiThreadId: string) => {
        return this.post(
            this.url.CANCEL_TURN.replace(":aiThreadId", String(aiThreadId)),
            { timeout: 15000 },
            {}
        );
    };

    public handleHumanReview = (aiThreadId: string, userId: string, action: string, data?: string, messageId?: string) => {
        const requestId = `hitl_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const parsedData = typeof data === "string" ? JSON.parse(data || "{}") : (data || {});
        
        const requestBody = {
            ai_thread_id: aiThreadId,
            user_id: userId,
            request_id: requestId,
            request: {
                human_review: {
                    action,
                    ...parsedData
                }
            }
        };
        
        return this.post(
            this.url.HANDLE_MESSAGE,
            { timeout: HANDLE_MESSAGE_HTTP_TIMEOUT_MS },
            requestBody
        );
    };

    public handleExportAction = (
        aiThreadId: string,
        userId: string,
        actionName: string,
        paramsObj: Record<string, unknown>,
        requestId?: string
    ) => {
        const resolvedRequestId = requestId || `exp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        return this.post(
            this.url.HANDLE_MESSAGE,
            { timeout: HANDLE_MESSAGE_HTTP_TIMEOUT_MS },
            {
                ai_thread_id: aiThreadId,
                user_id: userId,
                request_id: resolvedRequestId,
                request: {
                    export_action_request: {
                        action_name: actionName,
                        params_json: JSON.stringify(paramsObj || {}),
                    },
                },
            }
        );
    };

    public downloadTableAsPdf = (
        aiThreadId: string,
        messageId: string | null,
        title: string,
        headers: string[],
        rows: string[][],
        subtitle?: string | null
    ) => {
        return this.post(this.url.DOWNLOAD_TABLE_PDF, {}, {
            ai_thread_id: aiThreadId,
            message_id: messageId,
            title,
            headers,
            rows,
            subtitle: String(subtitle ?? "").trim(),
        }) as Promise<{ url: string; object_name: string }>;
    };

    public downloadThreadCsvExport = (aiThreadId: string, exportId: string) =>
        this.getBlob(
            API_URLS.DOWNLOAD_THREAD_CSV_EXPORT
                .replace(":aiThreadId", encodeURIComponent(aiThreadId))
                .replace(":exportId", encodeURIComponent(exportId))
        );

    public downloadThreadPdfExport = (aiThreadId: string, exportId: string, filename?: string) =>
        this.getBlob(
            API_URLS.DOWNLOAD_THREAD_PDF_EXPORT
                .replace(":aiThreadId", encodeURIComponent(aiThreadId))
                .replace(":exportId", encodeURIComponent(exportId)) +
                (filename ? `?filename=${encodeURIComponent(filename)}` : "")
        );

    public fileLinearTask = (aiThreadId: string, payload: FileLinearTaskRequest) =>
        this.post(
            API_URLS.FILE_LINEAR_TASK.replace(":aiThreadId", encodeURIComponent(aiThreadId)),
            { timeout: HANDLE_MESSAGE_HTTP_TIMEOUT_MS },
            payload
        ) as Promise<FileLinearTaskResponse>;

    public getLinearTaskAssignees = () =>
        this.get(API_URLS.GET_LINEAR_TASK_ASSIGNEES) as Promise<{ assignees: LinearTaskAssignee[] }>;

    public getLinearTaskTraceUrl = (aiThreadId: string) =>
        this.get(
            API_URLS.GET_LINEAR_TASK_TRACE_URL.replace(":aiThreadId", encodeURIComponent(aiThreadId))
        ) as Promise<{ trace_id: string; trace_url: string }>;

    /** User-initiated remote shell disconnect; backend releases relay mapping and emits SESSION_CLOSED via SSE. */
    public terminateRemoteShellSession = (aiThreadId: string, userId: string, requestId?: string) => {
        const resolvedRequestId =
            requestId || `ssh_disc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        return this.post(
            this.url.HANDLE_MESSAGE,
            { timeout: HANDLE_MESSAGE_HTTP_TIMEOUT_MS },
            {
                ai_thread_id: aiThreadId,
                user_id: userId,
                request_id: resolvedRequestId,
                request: {
                    terminate_remote_shell_session: {},
                },
            }
        );
    };

    public deleteAiThread = (aiThreadId: string) =>
        this.del(API_URLS.DELETE_AI_THREAD.replace(":aiThreadId", aiThreadId));

    public getToolAuditLogs = async (aiThreadId: string) => {
        const cached = getCached(threadLogsCache, aiThreadId);
        if (cached !== undefined) return cached;
        const data = await this.get(API_URLS.GET_TOOL_AUDIT_LOGS.replace(":aiThreadId", aiThreadId));
        setCached(threadLogsCache, aiThreadId, data);
        return data;
    };

    /** All audit logs for all threads of a ticket (for CSV export). */
    public getTicketToolAuditLogs = async (ticketId: string) => {
        const cached = getCached(ticketLogsCache, ticketId);
        if (cached !== undefined) return cached;
        const data = await this.get(API_URLS.GET_TICKET_TOOL_AUDIT_LOGS.replace(":ticketId", ticketId));
        setCached(ticketLogsCache, ticketId, data);
        return data;
    };

    /**
     * Preload tool audit logs for a thread in the background so the Audit logs popup opens quickly.
     * Fire-and-forget (async, does not block). Call when the user has a chat thread open.
     */
    public preloadToolAuditLogs = (aiThreadId: string | null): void => {
        if (!aiThreadId) return;
        void this.getToolAuditLogs(aiThreadId).catch(() => {});
    };

    /**
     * Preload ticket threads and ticket-level audit logs in the background for the Audit logs popup.
     * Fire-and-forget (async, does not block). Call when the user has a ticket open.
     */
    public preloadTicketAuditData = (ticketId: string | null): void => {
        if (!ticketId) return;
        void this.getTicketThreads(ticketId).catch(() => {});
        void this.getTicketToolAuditLogs(ticketId).catch(() => {});
    };

    /** Invalidate cached thread audit logs (e.g. after ai_complete so next open shows latest). */
    public invalidateThreadAuditCache = (aiThreadId: string | null): void => {
        if (!aiThreadId) return;
        threadLogsCache.delete(aiThreadId);
    };

    /** Invalidate cached ticket threads and ticket audit logs. */
    public invalidateTicketAuditCache = (ticketId: string | null): void => {
        if (!ticketId) return;
        ticketThreadsCache.delete(ticketId);
        ticketLogsCache.delete(ticketId);
    };

    /**
     * Refresh thread audit cache: invalidate then preload in background. Call on ai_complete.
     * Non-blocking (preload is fire-and-forget).
     */
    public refreshThreadAuditCache = (aiThreadId: string | null): void => {
        if (!aiThreadId) return;
        this.invalidateThreadAuditCache(aiThreadId);
        this.preloadToolAuditLogs(aiThreadId);
    };

    /**
     * Refresh ticket audit cache: invalidate then preload in background. Call on ai_complete when in ticket context.
     * Non-blocking (preload is fire-and-forget).
     */
    public refreshTicketAuditCache = (ticketId: string | null): void => {
        if (!ticketId) return;
        this.invalidateTicketAuditCache(ticketId);
        this.preloadTicketAuditData(ticketId);
    };

    public getUserThread = (userId: string) => 
        this.get(API_URLS.GET_USER_THREAD.replace(":userId", userId));

    public createUserThread = (
        userId: string,
        customerId: number | null = null,
        options?: CreateUserThreadOptions
    ) => {
        const body: any = { user_id: userId };
        if (customerId !== null) {
            body.customer_id = customerId;
        }
        if (options?.researchEnabled) {
            body.research_enabled = true;
        }
        return this.post(API_URLS.CREATE_USER_THREAD, {}, body);
    };

    public getUserThreads = (userId: string, options?: GetUserThreadsOptions) => {
        const base = API_URLS.GET_USER_THREADS.replace(":userId", userId);
        if (!options?.researchOnly) return this.get(base);
        return this.get(`${base}?research_enabled=1`);
    };

    public getAiThreadById = (aiThreadId: string) =>
        this.get(`/ai-threads/${aiThreadId}`);

    /** The thread's automation mode, for the Auto Mode badge; plain chat when Cortex cannot say. */
    public getThreadAutomation = (aiThreadId: string) =>
        this.getCoalesced(`/ai-threads/${encodeURIComponent(aiThreadId)}/automation`) as Promise<ThreadAutomation>;

    /** The durable Auto Resolver state for a ticket run attached to this thread. */
    public getThreadResolverState = (aiThreadId: string) =>
        this.get(`/ai-threads/${encodeURIComponent(aiThreadId)}/resolver-state`) as Promise<ThreadResolverState>;

    /** Enabled customer applications + MSP platforms for Chat Assistant thread header. */
    public getChatThreadIntegrationSurface = (aiThreadId: string) =>
        this.get(`/ai-threads/${aiThreadId}/integration-surface`);

    public renameUserThread = (aiThreadId: string, newTitle: string) =>
        this.patch(API_URLS.RENAME_USER_THREAD.replace(":aiThreadId", aiThreadId), {}, { title: newTitle });

    public updateAiThreadPinned = (aiThreadId: string, pinned: boolean) =>
        this.patch(`/ai-threads/${aiThreadId}/pinned`, {}, { pinned });

    public updateAiThreadShared = (aiThreadId: string, shared: boolean) =>
        this.patch(`/ai-threads/${aiThreadId}/shared`, {}, { shared });

    public resolveInformationRequests = (aiThreadId: string) =>
        this.post(`/ai-threads/${aiThreadId}/information-request/resolve`, {});

    /**
     * Trigger an agentic undo for the given call_ids. Posts a normal user message to
     * HANDLE_MESSAGE with an undo_action_request body; the server transforms it into
     * a "Please undo…" text message before forwarding to Cortex via gRPC.
     */
    public handleUndoAction = (aiThreadId: string, userEmail: string, callIds: string[]) => {
        const request_id = `undo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        return this.post(
            this.url.HANDLE_MESSAGE,
            { timeout: HANDLE_MESSAGE_HTTP_TIMEOUT_MS },
            {
                ai_thread_id: aiThreadId,
                user_id: userEmail,
                request_id,
                request: {
                    undo_action_request: {
                        call_ids: callIds,
                    },
                },
            }
        );
    };

}

export default ChatApi;

/** Shared instance used by ToolAuditLogPopup and by preload callers so audit log cache is shared. */
export const chatApiInstance = new ChatApi();
